import React, { useEffect, useRef, useState } from "react";
import { HamburgerMenuIcon, CloseIcon } from "../../common/Icons";
import { HeaderProps } from "../../common/types";

const Header: React.FC<HeaderProps> = ({
  userName,
  lastName,
  onMenuToggle,
  isMobileMenuOpen,
  currentViewedStepId,
  onLogoClick,
  userEmail,
  showMenuButton = true,
}) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Format short name
  const shortName =
    userName?.trim().split(" ")[0].length > 8
      ? `${userName.trim().split(" ")[0].slice(0, 6)}...`
      : (userName?.trim().split(" ")[0] ?? "");
  const firstLetter = userName ? userName.trim()[0].toUpperCase() : "?";
  const secondLetter = lastName ? lastName.trim()[0].toUpperCase() : "?";
  const trimmedEmail = userEmail
    ? userEmail.length > 20
      ? `${userEmail.slice(0, 19)}...`
      : userEmail
    : "";

  // Detect outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  return (
    <header>
      {/* Blue background section for logos */}
      <div className="bg-[#09517d] text-white shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-20 flex items-center justify-between">
            {/* Left section for menu button */}
            <div className="flex-1 flex justify-start">
              {showMenuButton &&
                currentViewedStepId !== 0 && ( // Only show button if not on Welcome Page
                  <button
                    onClick={onMenuToggle}
                    className="text-white p-2.5 mr-2 md:hidden"
                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-sidebar"
                  >
                    {isMobileMenuOpen ? (
                      <CloseIcon className="w-7 h-7" />
                    ) : (
                      <HamburgerMenuIcon className="w-7 h-7" />
                    )}
                  </button>
                )}
              <img
                src="media/images/veros-logo-big-white-only.png"
                alt="veros-logo-big-white-only"
                className="w-auto hidden md:block"
                style={{ height: "41px", border: "0" }}
              />
            </div>

            {/* Centered CustomerLane Logo */}
            <div className="flex-shrink-0">
              <img
                src="media/images/Customer-Lane-White-Less.png"
                alt="CustomerLane Logo"
                className="block w-auto h-auto max-h-[45px] md:max-h-[70px]"
              />
            </div>

            {/* Right Aligned: Veros Credit Logo */}
            <div
              className="flex-1 flex justify-end items-center relative"
              ref={dropdownRef}
            >
              <div className="relative">
                {/* DESKTOP: Hi Username + Avatar */}
                {userName && (
                  <div
                    className="hidden md:flex items-center gap-2 cursor-pointer"
                    onClick={() => setProfileOpen(!profileOpen)}
                  >
                    <span className="font-semibold">Hi {shortName}</span>

                    <div
                      className="w-10 h-10 bg-white text-[#09517d] font-bold 
                                flex items-center justify-center rounded-full"
                    >
                      {firstLetter}
                      {secondLetter}
                    </div>
                  </div>
                )}

                {/* MOBILE: 3 Dots only */}
                <div
                  className="md:hidden px-2 text-2xl cursor-pointer"
                  onClick={() => setProfileOpen(!profileOpen)}
                >
                  ⋮
                </div>

                {/* DROPDOWN MENU */}
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white text-black rounded-xl shadow-lg p-4 z-50">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 shrink-0 bg-[#09517d] text-white font-bold flex items-center justify-center rounded-full text-xl">
                        {firstLetter}
                        {secondLetter}
                      </div>

                      <div className="min-w-0 break-all">
                        <p className="font-semibold text-lg">{userName}</p>
                        <p
                          className="tooltip text-sm text-gray-600"
                          data-tooltip={userEmail}
                        >
                          {trimmedEmail}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={onLogout}
                      className="w-full bg-[#f1f1f1] text-[#09517d] font-semibold py-2 rounded-lg mt-4 hover:bg-gray-200"
                    >
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
